import Button from '@ui/buttons/Button'
import { colors } from '@ui/colors'
import ConfirmationModal from '@ui/components/ConfirmationModal'
import { useModalState } from '@order/hooks/useModalDataState'
import { CountdownModal } from '@screens/app/inactive'
import Typography from '@ui/typography'
import { leadingNone } from '@ui/typography/styles'
import { useLogout } from 'hooks/useLogout'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import cn from '@ui/utils/cn'

const DEFAULT_TIMEOUT_SECONDS = 5 * 60 // 5 minutes

const getTimeEnd = (seconds: number) => Date.now() + (seconds + 1) * 1000

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

const LogoutTimerButton = ({
  timeoutSeconds = DEFAULT_TIMEOUT_SECONDS,
  confirmation,
  modalThreshold = 30,
  className,
}: {
  className?: string
  modalThreshold?: number
  confirmation: boolean
  timeoutSeconds?: number
}) => {
  const { t } = useTranslation(['ui', 'payment'])
  const logout = useLogout()

  const { open, isOpen, close } = useModalState()

  const timeEnd = useRef<number>(getTimeEnd(timeoutSeconds))
  const [secondsLeft, setSecondsLeft] = useState(timeoutSeconds)

  useEffect(() => {
    const tick = () => {
      const newSecondsLeft = Math.round((timeEnd.current - Date.now()) / 1000)
      setSecondsLeft(newSecondsLeft)
    }

    const loop = () => {
      tick()
      timeout = setTimeout(loop, 1000)
    }

    let timeout = setTimeout(() => {
      loop()
    }, 1000)
    return () => clearTimeout(timeout)
  }, [])

  const extendedTimeout = useCallback(() => {
    setSecondsLeft(timeoutSeconds)
    timeEnd.current = getTimeEnd(timeoutSeconds)
  }, [timeoutSeconds])

  const onModalConfirm = useCallback(() => {
    close()
    logout()
  }, [close, logout])

  const text = 'payment:status'

  return (
    <>
      <Button
        variant="outlined"
        overrideColorFront={colors.red[600]}
        className={cn('w-max border-red-600 pr-3', className)}
        label={t('ui:logout')}
        suffix={
          <View className="flex h-6 items-center justify-center rounded-xl bg-red-100 px-3">
            <View
              className="flex flex-row items-center justify-center gap-0"
              style={{ paddingTop: 1 }}
            >
              {formatTime(secondsLeft)
                ?.toString()
                .split('')
                .map((char, index) => {
                  const width = char === ':' ? 8 : 11
                  const textAlign = 'center'
                  return (
                    <Typography
                      key={`${char}-${index}`}
                      variant="MDB"
                      className={cn('color-red-600', leadingNone)}
                      style={{
                        minWidth: width,
                        maxWidth: width,
                        width,
                        textAlign,
                      }}
                    >
                      {char}
                    </Typography>
                  )
                })}
            </View>
          </View>
        }
        onPress={confirmation ? open : logout}
      />
      <CountdownModal
        open={secondsLeft <= modalThreshold}
        onClose={extendedTimeout}
        timeoutSeconds={modalThreshold}
      />
      <ConfirmationModal
        open={isOpen}
        onClose={close}
        onConfirm={onModalConfirm}
        title={t(`${text}.confirm.logout.title`)}
        desc={t(`${text}.confirm.logout.desc`)}
      />
    </>
  )
}

export default LogoutTimerButton
